import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Calendar, MapPin, Clock, Users, Check, LogIn,
  CalendarDays, Tag, Mail, Lock, Crown, Hourglass, Ticket, Download,
} from "lucide-react";
import { toast } from "sonner";
import { labelEventType } from "@/lib/roleCategories";
const TYPE_LABEL = new Proxy({}, { get: (_, k) => labelEventType(k) });

const VISIBILITY_BADGES = {
  public: { label: "Ouvert à tous", color: "#7A8E3F", bg: "#E9EFD7" },
  private: { label: "Privé · sur invitation", color: "#1A1A1A", bg: "#EADDC9" },
  premium: { label: "Premium uniquement", color: "#8B5A2B", bg: "#F4E4C9" },
};

function eventCover(e) {
  if (!e) return null;
  if (e.cover_image) return `${API}/files/${e.cover_image}`;
  if (e.image_url) return e.image_url;
  return null;
}

function fmtDate(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long", year: "numeric",
    });
  } catch { return s; }
}

export default function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rsvping, setRsvping] = useState(false);
  const [rsvpInfo, setRsvpInfo] = useState({ participants_count: 0, has_rsvped: false, my_status: null });
  const [confirmation, setConfirmation] = useState(false);
  const [ticket, setTicket] = useState(null);  // {event, participant} for confirmed users

  const loadTicket = async () => {
    if (!user) return;
    try {
      const { data } = await api.get(`/my/event-tickets/${id}`);
      setTicket(data);
    } catch { setTicket(null); }
  };

  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const { data } = await api.get(`/my/event-tickets/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `helencia-billet-${(event?.title || "evenement").toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Billet PDF téléchargé");
    } catch (e) {
      toast.error(e.userMessage || "Téléchargement impossible");
    } finally { setDownloadingPdf(false); }
  };

  const load = async () => {
    try {
      // Public event listing returns full info; find the one we need
      const { data: list } = await api.get("/events");
      const ev = (Array.isArray(list) ? list : []).find((e) => e.id === id);
      if (!ev) throw new Error("not found");
      setEvent(ev);
      setRsvpInfo({
        participants_count: ev.participants_count || 0,
        has_rsvped: ev.has_rsvped || false,
        my_status: ev.my_status || null,
      });
      if (user && ev.has_rsvped) loadTicket();
    } catch {
      toast.error("Événement introuvable");
      navigate("/events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Reactively load the ticket once auth resolves and user is confirmed.
  useEffect(() => {
    if (user && rsvpInfo.has_rsvped && !ticket) loadTicket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, rsvpInfo.has_rsvped]);

  const handleRsvp = async () => {
    if (!user) {
      const next = encodeURIComponent(window.location.pathname);
      navigate(`/login?next=${next}`);
      return;
    }
    setRsvping(true);
    try {
      const { data } = await api.post(`/events/${id}/rsvp`);
      setRsvpInfo((r) => ({
        participants_count: r.participants_count + (data.registered && data.status === "confirmed" ? 1 : (!data.registered && r.my_status === "confirmed" ? -1 : 0)),
        has_rsvped: data.registered,
        my_status: data.registered ? data.status : null,
      }));
      if (data.registered) {
        setConfirmation(true);
        const msg = {
          confirmed: "Inscription confirmée — votre billet est prêt",
          waitlist:  "Vous êtes en liste d'attente",
          pending:   "Demande envoyée — en attente d'approbation",
        }[data.status] || "Inscription enregistrée";
        toast.success(msg);
        if (data.status === "confirmed") loadTicket();
      } else {
        toast.info("Inscription annulée");
        setTicket(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setRsvping(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }
  if (!event) return null;

  const cover = eventCover(event);
  const past = event.status === "past";

  return (
    <AppShell>
      <SEO
        title={`${event.title} — Événement`}
        description={(event.description || "Événement Helencia").toString().slice(0, 200)}
        image={typeof window !== "undefined" ? `${window.location.origin}/api/og/events/${event.id}.png` : undefined}
        url={typeof window !== "undefined" ? `${window.location.origin}/events/${event.id}` : undefined}
        type="event"
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-[#6E4B2A] hover:text-[#1A1A1A] mb-6 mt-6"
          data-testid="event-back"
        >
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>

        {/* Hero card */}
        <div className="rounded-3xl overflow-hidden bg-white border border-[#EADDC9] shadow-[0_20px_60px_-30px_rgba(110,75,42,0.25)]">
          {cover && (
            <div className="aspect-[16/8] overflow-hidden bg-[#FAF3EA] relative">
              <img src={cover} alt={event.title} className="w-full h-full object-cover" data-testid="event-cover" />
              <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-white/95 px-3 py-1.5 rounded-full text-[10px] uppercase tracking-wider font-semibold text-[#1A1A1A]">
                <Tag className="w-3 h-3 text-[#C68E59]" /> {TYPE_LABEL[event.event_type] || event.event_type}
              </span>
            </div>
          )}

          <div className="p-6 sm:p-10">
            {/* Visibility badge */}
            {(() => {
              const v = VISIBILITY_BADGES[event.visibility || "public"];
              return v ? (
                <span
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold px-2.5 py-1 rounded-full mb-4"
                  style={{ backgroundColor: v.bg, color: v.color }}
                  data-testid={`event-visibility-${event.visibility || "public"}`}
                >
                  {event.visibility === "private" && <Lock className="w-3 h-3" />}
                  {event.visibility === "premium" && <Crown className="w-3 h-3" />}
                  {v.label}
                  {event.capacity && <span> · {event.capacity} places</span>}
                </span>
              ) : null;
            })()}
            <h1 className="font-serif text-3xl sm:text-5xl text-[#1A1A1A] leading-tight" data-testid="event-title">
              {event.title}
            </h1>

            {event.description && (
              <p className="text-[#4A4A4A] leading-relaxed mt-5 max-w-3xl whitespace-pre-line">
                {event.description}
              </p>
            )}

            {/* Meta grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-[#EADDC9]">
              <Meta icon={Calendar} label="Date" value={fmtDate(event.date)} />
              {event.time && <Meta icon={Clock} label="Heure" value={event.time} />}
              <Meta icon={MapPin} label="Lieu" value={event.location || "À venir"} />
              <Meta icon={Users} label="Participants" value={`${rsvpInfo.participants_count}`} testid="event-participants" />
            </div>

            {/* RSVP CTA */}
            <div className="mt-8">
              {past ? (
                <div className="inline-flex items-center gap-2 text-sm text-[#8B8680] bg-[#FAF3EA] border border-[#EADDC9] px-4 py-2.5 rounded-full">
                  Cet événement est terminé
                </div>
              ) : !user ? (
                <Button
                  onClick={handleRsvp}
                  className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-7 h-12 text-base"
                  data-testid="event-login-cta"
                >
                  <LogIn className="w-4 h-4 mr-2" /> Connectez-vous pour participer
                </Button>
              ) : rsvpInfo.has_rsvped ? (
                <div className="flex flex-wrap items-center gap-3" data-testid="event-already-rsvped">
                  {rsvpInfo.my_status === "confirmed" && (
                    <div className="inline-flex items-center gap-2 text-sm text-[#2E7D32] bg-[#E7F5EC] border border-[#BFE4C5] px-4 py-2.5 rounded-full">
                      <Check className="w-4 h-4" /> Inscription confirmée
                    </div>
                  )}
                  {rsvpInfo.my_status === "waitlist" && (
                    <div className="inline-flex items-center gap-2 text-sm text-[#8B5A2B] bg-[#F4E4C9] border border-[#E6C29A] px-4 py-2.5 rounded-full">
                      <Hourglass className="w-4 h-4" /> Sur liste d'attente
                    </div>
                  )}
                  {rsvpInfo.my_status === "pending" && (
                    <div className="inline-flex items-center gap-2 text-sm text-[#3F4B7A] bg-[#E5E5F0] border border-[#C7C8E2] px-4 py-2.5 rounded-full">
                      <Lock className="w-4 h-4" /> En attente d'approbation
                    </div>
                  )}
                  <Button
                    onClick={handleRsvp}
                    disabled={rsvping}
                    variant="outline"
                    className="rounded-full border-[#EADDC9] text-[#A3483F] hover:bg-[#FCE9E7]"
                    data-testid="event-cancel-rsvp"
                  >
                    Annuler ma participation
                  </Button>
                </div>
              ) : (
                <Button
                  onClick={handleRsvp}
                  disabled={rsvping}
                  className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-7 h-12 text-base"
                  data-testid="event-rsvp-btn"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  {rsvping ? "Inscription…" : "Participer à l'événement"}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Confirmation dialog (in-page banner after RSVP) */}
        {confirmation && (
          <div className="mt-6 rounded-2xl bg-[#FFFBF5] border border-[#E6C29A] p-5 flex flex-col sm:flex-row gap-4 items-start" data-testid="event-confirmation">
            <div className="w-10 h-10 rounded-full bg-[#C68E59] flex items-center justify-center text-white flex-shrink-0">
              <Mail className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="font-serif text-lg text-[#1A1A1A]">Confirmation envoyée</p>
              <p className="text-sm text-[#6E4B2A] leading-relaxed mt-1">
                Un email récapitulatif vous a été envoyé avec toutes les informations utiles. Retrouvez aussi cet événement dans <Link to="/talent/my-events" className="underline font-medium">Mes événements</Link>.
              </p>
            </div>
            <button
              onClick={() => setConfirmation(false)}
              className="text-xs text-[#6E4B2A] underline hover:text-[#1A1A1A] mt-2 sm:mt-0"
            >
              Fermer
            </button>
          </div>
        )}

        {/* Ticket QR */}
        {ticket?.participant?.status === "confirmed" && ticket.participant.ticket_token && (
          <div className="mt-6 rounded-3xl bg-white border border-[#EADDC9] p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-center sm:items-start" data-testid="event-ticket-card">
            <div className="flex-shrink-0">
              <img
                src={`${API}/events/${id}/ticket.png?token=${encodeURIComponent(ticket.participant.ticket_token)}`}
                alt="Billet QR"
                className="w-44 h-44 rounded-2xl border border-[#EADDC9] bg-[#FAF3EA]"
                data-testid="event-ticket-qr"
              />
            </div>
            <div className="flex-1 text-center sm:text-left">
              <p className="caption-label text-[#A26F3E] inline-flex items-center gap-1.5">
                <Ticket className="w-3.5 h-3.5" /> Votre billet Helencia
              </p>
              <h3 className="font-serif text-2xl text-[#1A1A1A] mt-1">{event.title}</h3>
              <p className="text-sm text-[#666] mt-1">{fmtDate(event.date)}{event.time && ` · ${event.time}`}{event.location && ` · ${event.location}`}</p>
              <p className="text-xs text-[#8B8680] mt-4 font-mono break-all">
                {ticket.participant.ticket_token}
              </p>
              <p className="text-xs text-[#8B8680] mt-4 leading-relaxed">
                Présentez ce QR code à l'entrée — l'équipe le scannera pour vous enregistrer. Le billet est nominatif et lié à votre compte Helencia.
              </p>
              <Button
                onClick={downloadPdf}
                disabled={downloadingPdf}
                className="mt-5 rounded-full bg-[#1A1A1A] hover:bg-[#3A3A3A] text-white px-5 h-10"
                data-testid="event-ticket-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloadingPdf ? "Génération…" : "Télécharger le billet PDF"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Meta({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#A26F3E]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-[#1A1A1A] font-medium mt-1.5 text-sm">{value}</div>
    </div>
  );
}
